import IntranetLayout from "@/components/IntranetLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Handshake, Plus, Phone, Mail, Globe, Search, Shield, ClipboardCheck,
  Calendar, AlertTriangle, CheckCircle2, XCircle, Clock, FileText,
  ChevronRight, ArrowLeft, Edit, Trash2, Eye, Building2, MapPin,
  ShieldCheck, BarChart3, Users, Star, Filter, Download
} from "lucide-react";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";

/* ─── Constants ─── */
const VENDOR_CATEGORIES = [
  "Marine Services", "Logistics", "Fuel & Lubricants", "Catering",
  "Safety Equipment", "IT & Communications", "Legal", "Insurance",
  "Recruitment", "Engineering", "Environmental", "Classification Society",
  "3rd Party Crewing", "Other"
];

const STATUS_MAP: Record<string, { label: string; color: string; icon: any }> = {
  approved: { label: "Approved", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", icon: CheckCircle2 },
  pending: { label: "Pending Review", color: "bg-amber-500/10 text-amber-600 border-amber-500/20", icon: Clock },
  suspended: { label: "Suspended", color: "bg-red-500/10 text-red-600 border-red-500/20", icon: XCircle },
  blacklisted: { label: "Blacklisted", color: "bg-red-700/10 text-red-700 border-red-700/20", icon: AlertTriangle },
};

const RISK_MAP: Record<string, { label: string; color: string }> = {
  low: { label: "Low", color: "bg-emerald-500/10 text-emerald-600" },
  medium: { label: "Medium", color: "bg-amber-500/10 text-amber-600" },
  high: { label: "High", color: "bg-orange-500/10 text-orange-600" },
  critical: { label: "Critical", color: "bg-red-500/10 text-red-600" },
};

const AUDIT_TYPES = ["initial", "annual", "follow_up", "special"];
const AUDIT_STATUS = ["scheduled", "in_progress", "completed", "overdue", "cancelled"];

const QUESTIONNAIRE_CATEGORIES = [
  { value: "iso9001", label: "ISO 9001 — Quality Management" },
  { value: "cybersecurity", label: "ISO 27001 — Cybersecurity" },
  { value: "hse", label: "HSE — Health, Safety & Environment" },
  { value: "environmental", label: "ISO 14001 — Environmental" },
  { value: "financial", label: "Financial & Anti-Bribery" },
  { value: "mlc_crewing", label: "MLC 2006 — Maritime Crewing" },
  { value: "general", label: "General Assessment" },
];

/* ─── Default ISO-aligned questionnaire templates ─── */
const DEFAULT_TEMPLATES: Record<string, { question: string; weight: number }[]> = {
  iso9001: [
    { question: "Does the vendor have a documented Quality Management System (QMS)?", weight: 10 },
    { question: "Is the vendor ISO 9001 certified? If yes, provide certificate number.", weight: 10 },
    { question: "Does the vendor maintain documented procedures for non-conformance and corrective actions?", weight: 8 },
    { question: "Is there evidence of management review of quality objectives?", weight: 7 },
    { question: "Does the vendor have a process for customer feedback and complaint handling?", weight: 7 },
    { question: "Are internal audits conducted at planned intervals?", weight: 8 },
    { question: "Does the vendor maintain records of staff competency and training?", weight: 7 },
    { question: "Is there a documented process for supplier/sub-contractor evaluation?", weight: 6 },
    { question: "Does the vendor have a document control procedure?", weight: 6 },
    { question: "Are process performance metrics tracked and reviewed?", weight: 6 },
  ],
  cybersecurity: [
    { question: "Does the vendor have an Information Security Management System (ISMS)?", weight: 10 },
    { question: "Is the vendor ISO 27001 certified?", weight: 10 },
    { question: "Are access controls implemented for sensitive systems and data?", weight: 9 },
    { question: "Does the vendor have an incident response plan for cyber events?", weight: 9 },
    { question: "Is data encrypted in transit and at rest?", weight: 8 },
    { question: "Are regular vulnerability assessments and penetration tests conducted?", weight: 8 },
    { question: "Does the vendor have a business continuity and disaster recovery plan?", weight: 8 },
    { question: "Are employees trained on cybersecurity awareness?", weight: 7 },
    { question: "Does the vendor comply with GDPR or equivalent data protection regulations?", weight: 7 },
    { question: "Is there a process for managing third-party access to systems?", weight: 7 },
  ],
  hse: [
    { question: "Does the vendor have a documented HSE policy signed by senior management?", weight: 10 },
    { question: "Are risk assessments conducted for all work activities?", weight: 9 },
    { question: "Does the vendor maintain incident and near-miss reporting systems?", weight: 9 },
    { question: "Are safety statistics (LTI, TRIR) tracked and reported?", weight: 8 },
    { question: "Is there a permit-to-work system in place?", weight: 8 },
    { question: "Does the vendor conduct regular safety toolbox talks?", weight: 7 },
    { question: "Are emergency response procedures documented and tested?", weight: 8 },
    { question: "Do personnel hold required safety certifications (BOSIET, HUET, etc.)?", weight: 8 },
    { question: "Is PPE provided and maintained to appropriate standards?", weight: 7 },
    { question: "Does the vendor have a drug and alcohol policy?", weight: 7 },
  ],
  environmental: [
    { question: "Does the vendor have an Environmental Management System (EMS)?", weight: 10 },
    { question: "Is the vendor ISO 14001 certified?", weight: 10 },
    { question: "Are environmental aspects and impacts identified and controlled?", weight: 8 },
    { question: "Does the vendor have a waste management and disposal procedure?", weight: 8 },
    { question: "Are spill prevention and response measures in place?", weight: 8 },
    { question: "Does the vendor track and report emissions (CO₂, SOx, NOx)?", weight: 7 },
    { question: "Is there a process for managing hazardous materials?", weight: 8 },
    { question: "Does the vendor comply with MARPOL and local environmental regulations?", weight: 9 },
  ],
  financial: [
    { question: "Does the vendor have anti-bribery and corruption policies?", weight: 10 },
    { question: "Are financial statements audited by an independent body?", weight: 8 },
    { question: "Does the vendor comply with sanctions screening requirements?", weight: 9 },
    { question: "Is there a conflict of interest declaration process?", weight: 7 },
    { question: "Does the vendor maintain adequate professional indemnity insurance?", weight: 8 },
    { question: "Are payment terms and pricing structures transparent and documented?", weight: 7 },
    { question: "Does the vendor have a modern slavery statement/policy?", weight: 8 },
  ],
  general: [
    { question: "How long has the vendor been in business?", weight: 5 },
    { question: "Does the vendor have experience in the offshore/maritime sector?", weight: 8 },
    { question: "Are references available from previous clients?", weight: 6 },
    { question: "Does the vendor have sufficient capacity to meet our requirements?", weight: 7 },
    { question: "Is the vendor's geographical coverage adequate for our operations?", weight: 6 },
    { question: "Does the vendor demonstrate commitment to continuous improvement?", weight: 6 },
  ],
  mlc_crewing: [
    { question: "Does the crewing agency hold a valid MLC 2006 DMLC Part I & II certificate?", weight: 10 },
    { question: "Is the agency licensed/registered by the flag state(s) in which it operates?", weight: 10 },
    { question: "Does the agency comply with ILO Convention No. 179 (Recruitment & Placement)?", weight: 9 },
    { question: "Are Seafarers' Employment Agreements (SEA) compliant with MLC Standard A2.1?", weight: 10 },
    { question: "Does the agency verify and maintain valid STCW certificates for all seafarers?", weight: 10 },
    { question: "Are pre-employment medical examinations conducted per MLC Standard A1.2?", weight: 9 },
    { question: "Does the agency maintain a seafarer complaint/grievance procedure per MLC Standard A5.1.5?", weight: 8 },
    { question: "Are wages paid monthly and in accordance with MLC Standard A2.2?", weight: 9 },
    { question: "Does the agency provide or verify P&I Club cover for repatriation (MLC Standard A2.5)?", weight: 8 },
    { question: "Is there a documented process for verifying seafarer identity documents and visas?", weight: 8 },
    { question: "Does the agency conduct drug & alcohol screening compliant with OCIMF/OVID standards?", weight: 8 },
    { question: "Are hours of work and rest monitored and recorded per MLC Standard A2.3 / STCW?", weight: 9 },
    { question: "Does the agency provide social security protection or equivalent (MLC Standard A4.5)?", weight: 7 },
    { question: "Are crew change and travel arrangements compliant with MLC repatriation requirements?", weight: 8 },
    { question: "Does the agency maintain a database tracking certificate expiry and renewal?", weight: 7 },
    { question: "Is there a documented equal-opportunity and anti-discrimination policy for recruitment?", weight: 7 },
    { question: "Does the agency have a process for handling abandonment-of-seafarers scenarios?", weight: 8 },
    { question: "Are fitness-for-duty assessments (mental health, fatigue) part of the placement process?", weight: 7 },
    { question: "Does the agency conduct background/reference checks on all placed seafarers?", weight: 7 },
    { question: "Is the agency subject to regular flag-state or port-state inspections, with records available?", weight: 8 },
  ],
};

/* ─── Component ─── */
const EmployeeVendors = () => {
  const { user, fullName } = useAuth();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState("registry");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editVendor, setEditVendor] = useState<any>(null);

  // Audit dialog
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditForm, setAuditForm] = useState<any>({ vendor_id: "", audit_type: "annual", scheduled_date: "", auditor_name: "" });

  // Questionnaire dialog
  const [questionnaireDialogOpen, setQuestionnaireDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("iso9001");
  const [questionnaireVendor, setQuestionnaireVendor] = useState("");
  const [questionnaireAudit, setQuestionnaireAudit] = useState("");
  const [questionResponses, setQuestionResponses] = useState<Record<number, { answer: string; score: number }>>({});

  // Scope dialog
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [scopeForm, setScopeForm] = useState<any>({ vendor_id: "", scope_name: "", description: "", valid_from: "", valid_until: "", conditions: "" });

  // Vendor form
  const emptyForm = {
    name: "", category: "Other", contact_name: "", contact_email: "", contact_phone: "",
    website: "", notes: "", status: "pending", risk_rating: "medium", tax_id: "",
    registration_number: "", insurance_provider: "", insurance_expiry: "",
    iso_certifications: [] as string[], payment_terms: "", currency: "USD",
    address: "", country: "", city: ""
  };
  const [form, setForm] = useState(emptyForm);

  /* ─── Queries ─── */
  const { data: vendors = [], isLoading } = useQuery({
    queryKey: ["vendors"],
    queryFn: async () => {
      const { data, error } = await supabase.from("vendors").select("*").order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: audits = [] } = useQuery({
    queryKey: ["vendor-audits"],
    queryFn: async () => {
      const { data, error } = await supabase.from("vendor_audits").select("*").order("scheduled_date", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: questionnaires = [] } = useQuery({
    queryKey: ["vendor-questionnaires"],
    queryFn: async () => {
      const { data, error } = await supabase.from("vendor_questionnaires").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: scopes = [] } = useQuery({
    queryKey: ["vendor-scopes"],
    queryFn: async () => {
      const { data, error } = await supabase.from("vendor_scopes").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  /* ─── Mutations ─── */
  const vendorMutation = useMutation({
    mutationFn: async (values: any) => {
      const payload = {
        ...values,
        iso_certifications: values.iso_certifications || [],
      };
      if (editVendor) {
        const { error } = await supabase.from("vendors").update(payload).eq("id", editVendor.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("vendors").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
      toast.success(editVendor ? "Vendor updated" : "Vendor registered");
      setForm(emptyForm);
      setEditVendor(null);
      setDialogOpen(false);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const approveVendor = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("vendors").update({
        status,
        approval_date: status === "approved" ? new Date().toISOString() : null,
        approved_by: status === "approved" ? user?.id : null,
        approved_by_name: status === "approved" ? fullName : null,
      }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
      toast.success("Vendor status updated");
    },
  });

  const auditMutation = useMutation({
    mutationFn: async (values: any) => {
      const { error } = await supabase.from("vendor_audits").insert(values);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-audits"] });
      toast.success("Audit scheduled");
      setAuditDialogOpen(false);
      setAuditForm({ vendor_id: "", audit_type: "annual", scheduled_date: "", auditor_name: "" });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const updateAuditStatus = useMutation({
    mutationFn: async ({ id, status, findings, corrective, score }: any) => {
      const payload: any = { status };
      if (status === "completed") {
        payload.completed_date = new Date().toISOString().split("T")[0];
        if (findings) payload.findings_summary = findings;
        if (corrective) payload.corrective_actions = corrective;
        if (score !== undefined) payload.score = score;
      }
      const { error } = await supabase.from("vendor_audits").update(payload).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-audits"] });
      toast.success("Audit updated");
    },
  });

  const submitQuestionnaire = useMutation({
    mutationFn: async () => {
      const template = DEFAULT_TEMPLATES[selectedCategory] || [];
      const questions = template.map((q, i) => ({
        question: q.question,
        weight: q.weight,
        answer: questionResponses[i]?.answer || "",
        score: questionResponses[i]?.score || 0,
      }));
      const maxScore = template.reduce((sum, q) => sum + q.weight, 0);
      const totalScore = Object.values(questionResponses).reduce((sum, r) => sum + (r.score || 0), 0);

      const { error } = await supabase.from("vendor_questionnaires").insert({
        vendor_id: questionnaireVendor || null,
        audit_id: questionnaireAudit || null,
        template_name: QUESTIONNAIRE_CATEGORIES.find(c => c.value === selectedCategory)?.label || selectedCategory,
        category: selectedCategory,
        questions,
        is_template: false,
        completed_by: user?.id,
        completed_by_name: fullName,
        completed_at: new Date().toISOString(),
        total_score: totalScore,
        max_score: maxScore,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-questionnaires"] });
      toast.success("Questionnaire submitted");
      setQuestionnaireDialogOpen(false);
      setQuestionResponses({});
    },
    onError: (e: any) => toast.error(e.message),
  });

  const scopeMutation = useMutation({
    mutationFn: async (values: any) => {
      const { error } = await supabase.from("vendor_scopes").insert({
        ...values,
        approved_by: user?.id,
        approved_by_name: fullName,
        status: "approved",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-scopes"] });
      toast.success("Scope of work added");
      setScopeDialogOpen(false);
      setScopeForm({ vendor_id: "", scope_name: "", description: "", valid_from: "", valid_until: "", conditions: "" });
    },
    onError: (e: any) => toast.error(e.message),
  });

  /* ─── Filters ─── */
  const filtered = useMemo(() => {
    return vendors.filter((v: any) => {
      const matchSearch = v.name.toLowerCase().includes(search.toLowerCase()) ||
        v.category.toLowerCase().includes(search.toLowerCase()) ||
        (v.contact_name || "").toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === "all" || v.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [vendors, search, statusFilter]);

  /* ─── Stats ─── */
  const stats = useMemo(() => {
    const approved = vendors.filter((v: any) => v.status === "approved").length;
    const pending = vendors.filter((v: any) => v.status === "pending").length;
    const suspended = vendors.filter((v: any) => v.status === "suspended" || v.status === "blacklisted").length;
    const overdueAudits = audits.filter((a: any) => a.status === "overdue" || (a.status === "scheduled" && new Date(a.scheduled_date) < new Date())).length;
    return { total: vendors.length, approved, pending, suspended, overdueAudits };
  }, [vendors, audits]);

  const openEditVendor = (v: any) => {
    setEditVendor(v);
    setForm({
      name: v.name, category: v.category, contact_name: v.contact_name || "",
      contact_email: v.contact_email || "", contact_phone: v.contact_phone || "",
      website: v.website || "", notes: v.notes || "", status: v.status || "pending",
      risk_rating: v.risk_rating || "medium", tax_id: v.tax_id || "",
      registration_number: v.registration_number || "", insurance_provider: v.insurance_provider || "",
      insurance_expiry: v.insurance_expiry || "", iso_certifications: v.iso_certifications || [],
      payment_terms: v.payment_terms || "", currency: v.currency || "USD",
      address: v.address || "", country: v.country || "", city: v.city || "",
    });
    setDialogOpen(true);
  };

  const getVendorName = (id: string) => vendors.find((v: any) => v.id === id)?.name || "Unknown";

  /* ─── Render ─── */
  return (
    <IntranetLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-2xl text-foreground">Vendor Relationship Management</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">ISO 9001 · ISO 27001 · Vendor approval & audit management</p>
        </div>
      </div>

      {/* ─── KPI Strip ─── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: "Total Vendors", value: stats.total, icon: Building2, color: "text-teal" },
          { label: "Approved", value: stats.approved, icon: CheckCircle2, color: "text-emerald-500" },
          { label: "Pending", value: stats.pending, icon: Clock, color: "text-amber-500" },
          { label: "Suspended", value: stats.suspended, icon: XCircle, color: "text-red-500" },
          { label: "Overdue Audits", value: stats.overdueAudits, icon: AlertTriangle, color: "text-orange-500" },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`w-5 h-5 ${s.color} flex-shrink-0`} />
              <div>
                <p className="text-xs text-muted-foreground font-body">{s.label}</p>
                <p className="text-xl font-display text-foreground">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ─── Tabs ─── */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 bg-muted/50">
          <TabsTrigger value="registry">Vendor Registry</TabsTrigger>
          <TabsTrigger value="approvals">Approval Status</TabsTrigger>
          <TabsTrigger value="audits">Audit Schedule</TabsTrigger>
          <TabsTrigger value="questionnaires">Audit Questionnaires</TabsTrigger>
          <TabsTrigger value="scopes">Scopes of Work</TabsTrigger>
          <TabsTrigger value="crewing">3rd Party Crewing</TabsTrigger>
        </TabsList>

        {/* ═══════════ REGISTRY TAB ═══════════ */}
        <TabsContent value="registry">
          {selectedVendor ? (
            <VendorDetail
              vendor={selectedVendor}
              audits={audits.filter((a: any) => a.vendor_id === selectedVendor.id)}
              scopes={scopes.filter((s: any) => s.vendor_id === selectedVendor.id)}
              questionnaires={questionnaires.filter((q: any) => q.vendor_id === selectedVendor.id)}
              onBack={() => setSelectedVendor(null)}
              onEdit={() => openEditVendor(selectedVendor)}
              onApprove={(status: string) => approveVendor.mutate({ id: selectedVendor.id, status })}
            />
          ) : (
            <>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search vendors..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40"><Filter className="w-3 h-3 mr-2" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="blacklisted">Blacklisted</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => { setEditVendor(null); setForm(emptyForm); setDialogOpen(true); }} className="bg-teal hover:bg-teal-light text-white gap-2">
                  <Plus className="w-4 h-4" /> Register Vendor
                </Button>
              </div>

              {isLoading ? (
                <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
              ) : filtered.length === 0 ? (
                <Card><CardContent className="p-12 text-center"><Handshake className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">{search ? "No matching vendors" : "No vendors registered yet"}</p></CardContent></Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filtered.map((v: any) => {
                    const st = STATUS_MAP[v.status] || STATUS_MAP.pending;
                    const risk = RISK_MAP[v.risk_rating] || RISK_MAP.medium;
                    const StatusIcon = st.icon;
                    return (
                      <Card key={v.id} className="hover:shadow-md transition-shadow cursor-pointer group" onClick={() => setSelectedVendor(v)}>
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-display text-base text-foreground group-hover:text-teal transition-colors">{v.name}</h3>
                            <Badge className={`text-[10px] border ${st.color}`}><StatusIcon className="w-3 h-3 mr-1" />{st.label}</Badge>
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-[10px]">{v.category}</Badge>
                            <Badge className={`text-[10px] ${risk.color}`}>Risk: {risk.label}</Badge>
                          </div>
                          {v.contact_name && <p className="text-sm text-foreground font-body">{v.contact_name}</p>}
                          <div className="space-y-1 mt-2">
                            {v.contact_phone && <p className="flex items-center gap-1.5 text-xs text-muted-foreground font-body"><Phone className="w-3 h-3" />{v.contact_phone}</p>}
                            {v.contact_email && <p className="flex items-center gap-1.5 text-xs text-muted-foreground font-body"><Mail className="w-3 h-3" />{v.contact_email}</p>}
                            {(v.city || v.country) && <p className="flex items-center gap-1.5 text-xs text-muted-foreground font-body"><MapPin className="w-3 h-3" />{[v.city, v.country].filter(Boolean).join(", ")}</p>}
                          </div>
                          <div className="flex items-center justify-end mt-3">
                            <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-teal transition-colors" />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ═══════════ APPROVALS TAB ═══════════ */}
        <TabsContent value="approvals">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg text-foreground">Vendor Approval Registry</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-3 font-display text-foreground">Vendor</th>
                    <th className="text-left py-3 px-3 font-display text-foreground">Category</th>
                    <th className="text-left py-3 px-3 font-display text-foreground">Status</th>
                    <th className="text-left py-3 px-3 font-display text-foreground">Risk</th>
                    <th className="text-left py-3 px-3 font-display text-foreground">Approved By</th>
                    <th className="text-left py-3 px-3 font-display text-foreground">Approval Date</th>
                    <th className="text-left py-3 px-3 font-display text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((v: any) => {
                    const st = STATUS_MAP[v.status] || STATUS_MAP.pending;
                    const risk = RISK_MAP[v.risk_rating] || RISK_MAP.medium;
                    const StatusIcon = st.icon;
                    return (
                      <tr key={v.id} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-3 px-3 font-body text-foreground">{v.name}</td>
                        <td className="py-3 px-3"><Badge variant="outline" className="text-[10px]">{v.category}</Badge></td>
                        <td className="py-3 px-3"><Badge className={`text-[10px] border ${st.color}`}><StatusIcon className="w-3 h-3 mr-1" />{st.label}</Badge></td>
                        <td className="py-3 px-3"><Badge className={`text-[10px] ${risk.color}`}>{risk.label}</Badge></td>
                        <td className="py-3 px-3 font-body text-muted-foreground text-xs">{v.approved_by_name || "—"}</td>
                        <td className="py-3 px-3 font-body text-muted-foreground text-xs">{v.approval_date ? format(new Date(v.approval_date), "dd MMM yyyy") : "—"}</td>
                        <td className="py-3 px-3">
                          <div className="flex gap-1">
                            {v.status !== "approved" && (
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-600" onClick={() => approveVendor.mutate({ id: v.id, status: "approved" })}>Approve</Button>
                            )}
                            {v.status === "approved" && (
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-600" onClick={() => approveVendor.mutate({ id: v.id, status: "suspended" })}>Suspend</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ═══════════ AUDITS TAB ═══════════ */}
        <TabsContent value="audits">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-lg text-foreground">Audit Schedule</h2>
            <Button onClick={() => setAuditDialogOpen(true)} className="bg-teal hover:bg-teal-light text-white gap-2">
              <Plus className="w-4 h-4" /> Schedule Audit
            </Button>
          </div>

          {audits.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><ClipboardCheck className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">No audits scheduled</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {audits.map((a: any) => {
                const isOverdue = a.status === "scheduled" && new Date(a.scheduled_date) < new Date();
                const statusColor = a.status === "completed" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" :
                  isOverdue ? "bg-red-500/10 text-red-600 border-red-500/20" :
                  a.status === "in_progress" ? "bg-blue-500/10 text-blue-600 border-blue-500/20" :
                  a.status === "cancelled" ? "bg-muted text-muted-foreground border-border" :
                  "bg-amber-500/10 text-amber-600 border-amber-500/20";
                return (
                  <Card key={a.id}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-display text-sm text-foreground">{getVendorName(a.vendor_id)}</h3>
                            <Badge className={`text-[10px] border ${statusColor}`}>{isOverdue ? "Overdue" : a.status.replace("_", " ")}</Badge>
                            <Badge variant="outline" className="text-[10px] capitalize">{a.audit_type.replace("_", " ")}</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground font-body">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {format(new Date(a.scheduled_date), "dd MMM yyyy")}</span>
                            {a.auditor_name && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {a.auditor_name}</span>}
                            {a.score !== null && a.score !== undefined && <span className="flex items-center gap-1"><Star className="w-3 h-3" /> Score: {a.score}%</span>}
                          </div>
                          {a.findings_summary && <p className="text-xs text-muted-foreground/70 mt-1 font-body">{a.findings_summary}</p>}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {a.status === "scheduled" && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateAuditStatus.mutate({ id: a.id, status: "in_progress" })}>Start</Button>
                          )}
                          {(a.status === "scheduled" || a.status === "in_progress") && (
                            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => {
                                const findings = prompt("Findings summary (optional):");
                                const score = prompt("Audit score % (0-100):");
                                updateAuditStatus.mutate({ id: a.id, status: "completed", findings, score: score ? Number(score) : undefined });
                              }}>
                              Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ═══════════ QUESTIONNAIRES TAB ═══════════ */}
        <TabsContent value="questionnaires">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-lg text-foreground">Audit Questionnaires</h2>
              <p className="text-xs text-muted-foreground font-body mt-1">ISO 9001 · ISO 27001 · ISO 14001 · HSE · Financial compliance</p>
            </div>
            <Button onClick={() => { setQuestionResponses({}); setQuestionnaireDialogOpen(true); }} className="bg-teal hover:bg-teal-light text-white gap-2">
              <Plus className="w-4 h-4" /> New Assessment
            </Button>
          </div>

          {/* Template cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {QUESTIONNAIRE_CATEGORIES.map(cat => {
              const count = questionnaires.filter((q: any) => q.category === cat.value && !q.is_template).length;
              return (
                <Card key={cat.value} className="hover:shadow-sm transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-teal flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-display text-foreground truncate">{cat.label}</p>
                        <p className="text-xs text-muted-foreground font-body">{DEFAULT_TEMPLATES[cat.value]?.length || 0} questions · {count} completed</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Completed questionnaires */}
          <h3 className="font-display text-sm text-foreground mb-3">Completed Assessments</h3>
          {questionnaires.filter((q: any) => !q.is_template).length === 0 ? (
            <Card><CardContent className="p-8 text-center"><FileText className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" /><p className="text-sm text-muted-foreground font-body">No assessments completed yet</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {questionnaires.filter((q: any) => !q.is_template).map((q: any) => {
                const pct = q.max_score ? Math.round((q.total_score / q.max_score) * 100) : 0;
                const pctColor = pct >= 80 ? "text-emerald-600" : pct >= 60 ? "text-amber-600" : "text-red-600";
                return (
                  <Card key={q.id}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-display text-foreground">{q.template_name}</h4>
                          {q.vendor_id && <Badge variant="outline" className="text-[10px]">{getVendorName(q.vendor_id)}</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground font-body">
                          {q.completed_by_name && `By ${q.completed_by_name}`}
                          {q.completed_at && ` · ${format(new Date(q.completed_at), "dd MMM yyyy")}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-display ${pctColor}`}>{pct}%</p>
                        <p className="text-[10px] text-muted-foreground">{q.total_score}/{q.max_score}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ═══════════ SCOPES TAB ═══════════ */}
        <TabsContent value="scopes">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-lg text-foreground">Approved Scopes of Work</h2>
            <Button onClick={() => setScopeDialogOpen(true)} className="bg-teal hover:bg-teal-light text-white gap-2">
              <Plus className="w-4 h-4" /> Add Scope
            </Button>
          </div>

          {scopes.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><Shield className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">No scopes of work defined</p></CardContent></Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-3 font-display text-foreground">Vendor</th>
                    <th className="text-left py-3 px-3 font-display text-foreground">Scope</th>
                    <th className="text-left py-3 px-3 font-display text-foreground">Status</th>
                    <th className="text-left py-3 px-3 font-display text-foreground">Valid From</th>
                    <th className="text-left py-3 px-3 font-display text-foreground">Valid Until</th>
                    <th className="text-left py-3 px-3 font-display text-foreground">Approved By</th>
                  </tr>
                </thead>
                <tbody>
                  {scopes.map((s: any) => {
                    const expired = s.valid_until && new Date(s.valid_until) < new Date();
                    return (
                      <tr key={s.id} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-3 px-3 font-body text-foreground">{getVendorName(s.vendor_id)}</td>
                        <td className="py-3 px-3 font-body text-foreground">
                          <p className="font-medium">{s.scope_name}</p>
                          {s.description && <p className="text-xs text-muted-foreground">{s.description}</p>}
                        </td>
                        <td className="py-3 px-3">
                          <Badge className={`text-[10px] border ${expired ? "bg-red-500/10 text-red-600 border-red-500/20" : s.status === "approved" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-amber-500/10 text-amber-600 border-amber-500/20"}`}>
                            {expired ? "Expired" : s.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-3 text-xs text-muted-foreground font-body">{s.valid_from ? format(new Date(s.valid_from), "dd MMM yyyy") : "—"}</td>
                        <td className="py-3 px-3 text-xs text-muted-foreground font-body">{s.valid_until ? format(new Date(s.valid_until), "dd MMM yyyy") : "—"}</td>
                        <td className="py-3 px-3 text-xs text-muted-foreground font-body">{s.approved_by_name || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        {/* ═══════════ 3RD PARTY CREWING TAB ═══════════ */}
        <TabsContent value="crewing">
          {(() => {
            const crewingVendors = vendors.filter((v: any) => v.category === "3rd Party Crewing");
            const crewingQuestionnaires = questionnaires.filter((q: any) => q.category === "mlc_crewing" && !q.is_template);
            const crewingAudits = audits.filter((a: any) => crewingVendors.some((cv: any) => cv.id === a.vendor_id));
            const mlcTemplate = DEFAULT_TEMPLATES.mlc_crewing || [];
            const mlcMaxScore = mlcTemplate.reduce((s, q) => s + q.weight, 0);

            return (
              <div className="space-y-6">
                {/* Crewing KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: "Crewing Vendors", value: crewingVendors.length, icon: Users, color: "text-teal" },
                    { label: "MLC Assessments", value: crewingQuestionnaires.length, icon: ShieldCheck, color: "text-blue-500" },
                    { label: "Crewing Audits", value: crewingAudits.length, icon: ClipboardCheck, color: "text-amber-500" },
                    { label: "Approved", value: crewingVendors.filter((v: any) => v.status === "approved").length, icon: CheckCircle2, color: "text-emerald-500" },
                  ].map((s) => (
                    <Card key={s.label}>
                      <CardContent className="p-4 flex items-center gap-3">
                        <s.icon className={`w-5 h-5 ${s.color} flex-shrink-0`} />
                        <div>
                          <p className="text-xs text-muted-foreground font-body">{s.label}</p>
                          <p className="text-xl font-display text-foreground">{s.value}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* MLC Compliance Overview */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-teal" /> MLC 2006 Compliance Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground font-body mb-4">
                      Maritime Labour Convention (MLC 2006) compliance status for registered 3rd party crewing agencies. 
                      Assessment covers STCW certification, SEA agreements, repatriation, wages, hours of rest, medical fitness, and flag-state licensing.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {crewingVendors.length === 0 ? (
                        <div className="col-span-full text-center py-8">
                          <Users className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground font-body">No crewing vendors registered. Register a vendor with category "3rd Party Crewing" to get started.</p>
                        </div>
                      ) : crewingVendors.map((v: any) => {
                        const st = STATUS_MAP[v.status] || STATUS_MAP.pending;
                        const StatusIcon = st.icon;
                        const vQuests = crewingQuestionnaires.filter((q: any) => q.vendor_id === v.id);
                        const latestQ = vQuests[0];
                        const pct = latestQ && latestQ.max_score ? Math.round((latestQ.total_score / latestQ.max_score) * 100) : null;
                        const pctColor = pct !== null ? (pct >= 80 ? "text-emerald-600" : pct >= 60 ? "text-amber-600" : "text-red-600") : "text-muted-foreground";
                        const vAudits = crewingAudits.filter((a: any) => a.vendor_id === v.id);
                        const lastAudit = vAudits[0];

                        return (
                          <Card key={v.id} className="hover:shadow-sm transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-display text-sm text-foreground">{v.name}</h4>
                                <Badge className={`text-[10px] border ${st.color}`}><StatusIcon className="w-3 h-3 mr-1" />{st.label}</Badge>
                              </div>
                              <div className="space-y-1.5 text-xs font-body text-muted-foreground">
                                {v.contact_name && <p className="flex items-center gap-1.5"><Users className="w-3 h-3" />{v.contact_name}</p>}
                                {(v.city || v.country) && <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{[v.city, v.country].filter(Boolean).join(", ")}</p>}
                                {v.registration_number && <p className="flex items-center gap-1.5"><FileText className="w-3 h-3" />Reg: {v.registration_number}</p>}
                              </div>
                              <Separator className="my-3" />
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-display">MLC Score</p>
                                  <p className={`text-lg font-display ${pctColor}`}>{pct !== null ? `${pct}%` : "—"}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-display">Last Audit</p>
                                  <p className="text-xs font-body text-foreground">{lastAudit ? format(new Date(lastAudit.scheduled_date), "dd MMM yyyy") : "—"}</p>
                                </div>
                              </div>
                              <div className="flex gap-1 mt-3">
                                <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => { setSelectedVendor(v); setActiveTab("registry"); }}>
                                  <Eye className="w-3 h-3 mr-1" /> View
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => {
                                  setQuestionnaireVendor(v.id);
                                  setSelectedCategory("mlc_crewing");
                                  setQuestionResponses({});
                                  setQuestionnaireDialogOpen(true);
                                }}>
                                  <ClipboardCheck className="w-3 h-3 mr-1" /> MLC Assess
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* MLC Checklist Reference */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                      <FileText className="w-4 h-4 text-teal" /> MLC 2006 / STCW Checklist Reference
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                      {[
                        { area: "Flag State Licensing", ref: "MLC Reg. 1.4" },
                        { area: "Seafarer Employment Agreements", ref: "MLC Std. A2.1" },
                        { area: "Wages & Allotments", ref: "MLC Std. A2.2" },
                        { area: "Hours of Work & Rest", ref: "MLC Std. A2.3 / STCW A-VIII" },
                        { area: "Annual Leave", ref: "MLC Std. A2.4" },
                        { area: "Repatriation", ref: "MLC Std. A2.5" },
                        { area: "Manning Levels", ref: "MLC Reg. 2.7" },
                        { area: "Medical Certification", ref: "MLC Std. A1.2 / STCW A-I/9" },
                        { area: "STCW Certification", ref: "STCW Conv. Art. VI, Reg. I/2" },
                        { area: "Onboard Complaint Procedures", ref: "MLC Std. A5.1.5" },
                        { area: "Social Security Protection", ref: "MLC Std. A4.5" },
                        { area: "D&A Screening", ref: "OCIMF / OVID Guidance" },
                        { area: "Abandonment Financial Security", ref: "MLC Std. A2.5.2" },
                        { area: "Equal Opportunity", ref: "ILO Conv. 111 / MLC Preamble" },
                      ].map((item) => (
                        <div key={item.area} className="flex items-center justify-between py-1.5 border-b border-border/30">
                          <span className="text-xs font-body text-foreground">{item.area}</span>
                          <Badge variant="outline" className="text-[10px]">{item.ref}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Completed MLC Assessments */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-teal" /> Completed MLC Assessments ({crewingQuestionnaires.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {crewingQuestionnaires.length === 0 ? (
                      <p className="text-xs text-muted-foreground font-body py-4 text-center">No MLC assessments completed yet</p>
                    ) : (
                      <div className="space-y-2">
                        {crewingQuestionnaires.map((q: any) => {
                          const pct = q.max_score ? Math.round((q.total_score / q.max_score) * 100) : 0;
                          const pctColor = pct >= 80 ? "text-emerald-600" : pct >= 60 ? "text-amber-600" : "text-red-600";
                          return (
                            <div key={q.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/30">
                              <div>
                                <div className="flex items-center gap-2 mb-0.5">
                                  <h4 className="text-sm font-display text-foreground">{getVendorName(q.vendor_id)}</h4>
                                  <Badge variant="outline" className="text-[10px]">MLC 2006</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground font-body">
                                  {q.completed_by_name && `By ${q.completed_by_name}`}
                                  {q.completed_at && ` · ${format(new Date(q.completed_at), "dd MMM yyyy")}`}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className={`text-lg font-display ${pctColor}`}>{pct}%</p>
                                <p className="text-[10px] text-muted-foreground">{q.total_score}/{q.max_score}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>

      {/* ═══════════ VENDOR DIALOG ═══════════ */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditVendor(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editVendor ? "Edit Vendor" : "Register New Vendor"}</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); vendorMutation.mutate(form); }} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Input placeholder="Company Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>{VENDOR_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.risk_rating} onValueChange={(v) => setForm({ ...form, risk_rating: v })}>
                <SelectTrigger><SelectValue placeholder="Risk Rating" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low Risk</SelectItem>
                  <SelectItem value="medium">Medium Risk</SelectItem>
                  <SelectItem value="high">High Risk</SelectItem>
                  <SelectItem value="critical">Critical Risk</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />
            <p className="text-xs font-display text-muted-foreground uppercase tracking-wider">Contact Information</p>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Contact Name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              <Input placeholder="Contact Email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
              <Input placeholder="Contact Phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
              <Input placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </div>

            <Separator />
            <p className="text-xs font-display text-muted-foreground uppercase tracking-wider">Address</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <Input placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </div>

            <Separator />
            <p className="text-xs font-display text-muted-foreground uppercase tracking-wider">Compliance & Registration</p>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Tax ID / VAT Number" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} />
              <Input placeholder="Registration Number" value={form.registration_number} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} />
              <Input placeholder="Insurance Provider" value={form.insurance_provider} onChange={(e) => setForm({ ...form, insurance_provider: e.target.value })} />
              <Input type="date" placeholder="Insurance Expiry" value={form.insurance_expiry} onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })} />
              <Input placeholder="Payment Terms" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GYD">GYD</SelectItem>
                  <SelectItem value="TTD">TTD</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />
            <p className="text-xs font-display text-muted-foreground uppercase tracking-wider">Notes</p>
            <Textarea placeholder="Additional notes, certifications, or remarks..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />

            <Button type="submit" className="w-full bg-teal hover:bg-teal-light text-white" disabled={vendorMutation.isPending}>
              {editVendor ? "Update Vendor" : "Register Vendor"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ═══════════ AUDIT DIALOG ═══════════ */}
      <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule Vendor Audit</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); auditMutation.mutate(auditForm); }} className="space-y-4">
            <Select value={auditForm.vendor_id} onValueChange={(v) => setAuditForm({ ...auditForm, vendor_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select Vendor" /></SelectTrigger>
              <SelectContent>{vendors.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={auditForm.audit_type} onValueChange={(v) => setAuditForm({ ...auditForm, audit_type: v })}>
              <SelectTrigger><SelectValue placeholder="Audit Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="initial">Initial Assessment</SelectItem>
                <SelectItem value="annual">Annual Review</SelectItem>
                <SelectItem value="follow_up">Follow-Up</SelectItem>
                <SelectItem value="special">Special / Ad-Hoc</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={auditForm.scheduled_date} onChange={(e) => setAuditForm({ ...auditForm, scheduled_date: e.target.value })} required />
            <Input placeholder="Auditor Name" value={auditForm.auditor_name} onChange={(e) => setAuditForm({ ...auditForm, auditor_name: e.target.value })} />
            <Button type="submit" className="w-full bg-teal hover:bg-teal-light text-white" disabled={auditMutation.isPending || !auditForm.vendor_id}>Schedule Audit</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ═══════════ QUESTIONNAIRE DIALOG ═══════════ */}
      <Dialog open={questionnaireDialogOpen} onOpenChange={setQuestionnaireDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Vendor Assessment Questionnaire</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Select value={questionnaireVendor} onValueChange={setQuestionnaireVendor}>
                <SelectTrigger><SelectValue placeholder="Select Vendor" /></SelectTrigger>
                <SelectContent>{vendors.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); setQuestionResponses({}); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{QUESTIONNAIRE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <Separator />

            <div className="space-y-4">
              {(DEFAULT_TEMPLATES[selectedCategory] || []).map((q, i) => (
                <div key={i} className="p-3 rounded-lg bg-muted/30 border border-border/50">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-sm font-body text-foreground flex-1"><span className="font-display text-teal mr-2">Q{i + 1}.</span>{q.question}</p>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">Weight: {q.weight}</Badge>
                  </div>
                  <div className="grid grid-cols-[1fr_80px] gap-2">
                    <Input
                      placeholder="Answer / Evidence..."
                      value={questionResponses[i]?.answer || ""}
                      onChange={(e) => setQuestionResponses(prev => ({ ...prev, [i]: { ...prev[i], answer: e.target.value, score: prev[i]?.score || 0 } }))}
                    />
                    <Select
                      value={String(questionResponses[i]?.score || 0)}
                      onValueChange={(v) => setQuestionResponses(prev => ({ ...prev, [i]: { ...prev[i], answer: prev[i]?.answer || "", score: Number(v) } }))}
                    >
                      <SelectTrigger className="text-xs"><SelectValue placeholder="Score" /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: q.weight + 1 }, (_, s) => (
                          <SelectItem key={s} value={String(s)}>{s}/{q.weight}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="text-sm font-body text-muted-foreground">
                Total: <span className="font-display text-foreground">
                  {Object.values(questionResponses).reduce((sum, r) => sum + (r.score || 0), 0)}/
                  {(DEFAULT_TEMPLATES[selectedCategory] || []).reduce((sum, q) => sum + q.weight, 0)}
                </span>
              </div>
              <Button onClick={() => submitQuestionnaire.mutate()} disabled={submitQuestionnaire.isPending || !questionnaireVendor} className="bg-teal hover:bg-teal-light text-white">
                Submit Assessment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══════════ SCOPE DIALOG ═══════════ */}
      <Dialog open={scopeDialogOpen} onOpenChange={setScopeDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Approved Scope of Work</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); scopeMutation.mutate(scopeForm); }} className="space-y-4">
            <Select value={scopeForm.vendor_id} onValueChange={(v) => setScopeForm({ ...scopeForm, vendor_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select Vendor" /></SelectTrigger>
              <SelectContent>{vendors.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Scope Name *" value={scopeForm.scope_name} onChange={(e) => setScopeForm({ ...scopeForm, scope_name: e.target.value })} required />
            <Textarea placeholder="Description of approved scope..." value={scopeForm.description} onChange={(e) => setScopeForm({ ...scopeForm, description: e.target.value })} rows={2} />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground font-body">Valid From</label><Input type="date" value={scopeForm.valid_from} onChange={(e) => setScopeForm({ ...scopeForm, valid_from: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground font-body">Valid Until</label><Input type="date" value={scopeForm.valid_until} onChange={(e) => setScopeForm({ ...scopeForm, valid_until: e.target.value })} /></div>
            </div>
            <Textarea placeholder="Conditions / Limitations" value={scopeForm.conditions} onChange={(e) => setScopeForm({ ...scopeForm, conditions: e.target.value })} rows={2} />
            <Button type="submit" className="w-full bg-teal hover:bg-teal-light text-white" disabled={scopeMutation.isPending || !scopeForm.vendor_id}>Add Scope</Button>
          </form>
        </DialogContent>
      </Dialog>
    </IntranetLayout>
  );
};

/* ─── Vendor Detail Sub-Component ─── */
const VendorDetail = ({ vendor, audits, scopes, questionnaires, onBack, onEdit, onApprove }: {
  vendor: any; audits: any[]; scopes: any[]; questionnaires: any[];
  onBack: () => void; onEdit: () => void; onApprove: (status: string) => void;
}) => {
  const st = STATUS_MAP[vendor.status] || STATUS_MAP.pending;
  const risk = RISK_MAP[vendor.risk_rating] || RISK_MAP.medium;
  const StatusIcon = st.icon;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={onEdit} className="gap-1"><Edit className="w-3 h-3" /> Edit</Button>
        {vendor.status !== "approved" && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => onApprove("approved")}><CheckCircle2 className="w-3 h-3" /> Approve</Button>
        )}
        {vendor.status === "approved" && (
          <Button size="sm" variant="outline" className="text-amber-600 border-amber-300 gap-1" onClick={() => onApprove("suspended")}><XCircle className="w-3 h-3" /> Suspend</Button>
        )}
      </div>

      {/* Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="font-display text-xl text-foreground">{vendor.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs">{vendor.category}</Badge>
                <Badge className={`text-xs border ${st.color}`}><StatusIcon className="w-3 h-3 mr-1" />{st.label}</Badge>
                <Badge className={`text-xs ${risk.color}`}>Risk: {risk.label}</Badge>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            <div className="space-y-2">
              <p className="text-xs font-display text-muted-foreground uppercase tracking-wider">Contact</p>
              {vendor.contact_name && <p className="text-sm font-body text-foreground">{vendor.contact_name}</p>}
              {vendor.contact_email && <a href={`mailto:${vendor.contact_email}`} className="flex items-center gap-1.5 text-xs text-teal hover:underline font-body"><Mail className="w-3 h-3" />{vendor.contact_email}</a>}
              {vendor.contact_phone && <a href={`tel:${vendor.contact_phone}`} className="flex items-center gap-1.5 text-xs text-teal hover:underline font-body"><Phone className="w-3 h-3" />{vendor.contact_phone}</a>}
              {vendor.website && <a href={vendor.website} target="_blank" rel="noopener" className="flex items-center gap-1.5 text-xs text-teal hover:underline font-body"><Globe className="w-3 h-3" />{vendor.website}</a>}
            </div>
            <div className="space-y-2">
              <p className="text-xs font-display text-muted-foreground uppercase tracking-wider">Address</p>
              {vendor.address && <p className="text-sm font-body text-foreground">{vendor.address}</p>}
              {(vendor.city || vendor.country) && <p className="text-sm font-body text-muted-foreground">{[vendor.city, vendor.country].filter(Boolean).join(", ")}</p>}
            </div>
            <div className="space-y-2">
              <p className="text-xs font-display text-muted-foreground uppercase tracking-wider">Compliance</p>
              {vendor.tax_id && <p className="text-xs font-body text-foreground">Tax ID: {vendor.tax_id}</p>}
              {vendor.registration_number && <p className="text-xs font-body text-foreground">Reg: {vendor.registration_number}</p>}
              {vendor.insurance_provider && <p className="text-xs font-body text-foreground">Insurer: {vendor.insurance_provider}</p>}
              {vendor.insurance_expiry && <p className="text-xs font-body text-foreground">Insurance expires: {format(new Date(vendor.insurance_expiry), "dd MMM yyyy")}</p>}
              {vendor.payment_terms && <p className="text-xs font-body text-foreground">Terms: {vendor.payment_terms}</p>}
              {vendor.approved_by_name && <p className="text-xs font-body text-foreground">Approved by: {vendor.approved_by_name}</p>}
              {vendor.approval_date && <p className="text-xs font-body text-foreground">Approved: {format(new Date(vendor.approval_date), "dd MMM yyyy")}</p>}
            </div>
          </div>
          {vendor.notes && <p className="text-xs text-muted-foreground/70 font-body mt-4 p-3 bg-muted/30 rounded">{vendor.notes}</p>}
        </CardContent>
      </Card>

      {/* Scopes */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-display flex items-center gap-2"><Shield className="w-4 h-4 text-teal" /> Approved Scopes of Work ({scopes.length})</CardTitle></CardHeader>
        <CardContent>
          {scopes.length === 0 ? (
            <p className="text-xs text-muted-foreground font-body py-4 text-center">No scopes assigned</p>
          ) : (
            <div className="space-y-2">
              {scopes.map((s: any) => {
                const expired = s.valid_until && new Date(s.valid_until) < new Date();
                return (
                  <div key={s.id} className="flex items-center justify-between p-2 rounded bg-muted/20 border border-border/30">
                    <div>
                      <p className="text-sm font-body text-foreground">{s.scope_name}</p>
                      {s.description && <p className="text-xs text-muted-foreground">{s.description}</p>}
                    </div>
                    <Badge className={`text-[10px] ${expired ? "bg-red-500/10 text-red-600" : "bg-emerald-500/10 text-emerald-600"}`}>{expired ? "Expired" : "Active"}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audits */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-display flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-teal" /> Audit History ({audits.length})</CardTitle></CardHeader>
        <CardContent>
          {audits.length === 0 ? (
            <p className="text-xs text-muted-foreground font-body py-4 text-center">No audits recorded</p>
          ) : (
            <div className="space-y-2">
              {audits.map((a: any) => (
                <div key={a.id} className="flex items-center justify-between p-2 rounded bg-muted/20 border border-border/30">
                  <div>
                    <p className="text-sm font-body text-foreground capitalize">{a.audit_type.replace("_", " ")} Audit</p>
                    <p className="text-xs text-muted-foreground">{format(new Date(a.scheduled_date), "dd MMM yyyy")} {a.auditor_name && `· ${a.auditor_name}`}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.score !== null && a.score !== undefined && <span className="text-xs font-display text-foreground">{a.score}%</span>}
                    <Badge variant="outline" className="text-[10px] capitalize">{a.status.replace("_", " ")}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Questionnaires */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-display flex items-center gap-2"><FileText className="w-4 h-4 text-teal" /> Assessment History ({questionnaires.length})</CardTitle></CardHeader>
        <CardContent>
          {questionnaires.length === 0 ? (
            <p className="text-xs text-muted-foreground font-body py-4 text-center">No assessments completed</p>
          ) : (
            <div className="space-y-2">
              {questionnaires.map((q: any) => {
                const pct = q.max_score ? Math.round((q.total_score / q.max_score) * 100) : 0;
                return (
                  <div key={q.id} className="flex items-center justify-between p-2 rounded bg-muted/20 border border-border/30">
                    <div>
                      <p className="text-sm font-body text-foreground">{q.template_name}</p>
                      <p className="text-xs text-muted-foreground">{q.completed_by_name} · {q.completed_at && format(new Date(q.completed_at), "dd MMM yyyy")}</p>
                    </div>
                    <span className={`text-sm font-display ${pct >= 80 ? "text-emerald-600" : pct >= 60 ? "text-amber-600" : "text-red-600"}`}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeVendors;
